import React, { useState } from "react";

/**
 * Schema-rendering helpers + the recursive ResolvedSchema component.
 *
 * Why the self-references use `React.createElement(ResolvedSchema, …)`
 * instead of `<ResolvedSchema …/>`:
 *
 *   The `@emergentbase/visual-edits` Babel plugin (which CRACO injects
 *   into babel-loader) has a function `usageIsCompositePortal` at
 *   node_modules/@emergentbase/visual-edits/dist/babel-plugin/index.js:1369
 *   that walks every JSX `<Component>` and follows its binding's body
 *   to look for "portalish" components. The walker does NOT track
 *   visited bindings — so a self-recursive component (`<ResolvedSchema>`
 *   inside `function ResolvedSchema`) causes infinite recursion and a
 *   `Maximum call stack size exceeded` at compile time.
 *
 *   `React.createElement(...)` calls aren't `JSXOpeningElement` AST
 *   nodes, so the plugin's visitor never fires on them. Behaviour is
 *   identical to the JSX form at runtime.
 */

/** Resolve an OpenAPI $ref against the components dictionary. */
export function resolveRef(ref, spec, seen = new Set()) {
    if (!ref || typeof ref !== "string" || !ref.startsWith("#/")) return null;
    if (seen.has(ref)) return { _cyclic: true, $ref: ref };
    const path = ref.slice(2).split("/");
    let node = spec;
    for (const seg of path) {
        if (node == null) return null;
        node = node[seg];
    }
    if (node && node.$ref) {
        seen.add(ref);
        return resolveRef(node.$ref, spec, seen);
    }
    return node;
}

/** Walk a schema fragment and inline every `$ref` we encounter. */
export function inlineSchema(schema, spec, depth = 0) {
    if (!schema || typeof schema !== "object" || depth > 5) return schema;
    if (Array.isArray(schema)) return schema.map((s) => inlineSchema(s, spec, depth + 1));
    if (schema.$ref) {
        const resolved = resolveRef(schema.$ref, spec);
        return resolved ? inlineSchema(resolved, spec, depth + 1) : schema;
    }
    const out = {};
    for (const [k, v] of Object.entries(schema)) out[k] = inlineSchema(v, spec, depth + 1);
    return out;
}

/** Friendly Python-ish type label for a resolved property. */
export function describeType(prop) {
    if (!prop || typeof prop !== "object") return "any";
    if (prop.enum) return `enum: ${prop.enum.slice(0, 4).join(" | ")}${prop.enum.length > 4 ? " …" : ""}`;
    if (prop.anyOf || prop.oneOf) {
        const arr = (prop.anyOf || prop.oneOf).map((p) => describeType(p));
        const dedup = [...new Set(arr)];
        return dedup.join(" | ");
    }
    if (prop.allOf && prop.allOf.length) return describeType(prop.allOf[0]);
    if (prop.type === "array") return `array<${describeType(prop.items)}>`;
    if (prop.type === "null") return "null";
    if (prop.type) return prop.format ? `${prop.type}(${prop.format})` : prop.type;
    return "object";
}

/** Surfaces min/max/length/pattern/default on a single line. */
function describeConstraints(prop) {
    if (!prop) return "";
    const parts = [];
    if (prop.minLength != null || prop.maxLength != null)
        parts.push(`len: ${prop.minLength ?? 0}–${prop.maxLength ?? "∞"}`);
    if (prop.minimum != null || prop.maximum != null)
        parts.push(`range: ${prop.minimum ?? "-∞"}–${prop.maximum ?? "∞"}`);
    if (prop.pattern) parts.push(`pattern: ${prop.pattern}`);
    if (prop.default !== undefined && prop.default !== null)
        parts.push(`default: ${typeof prop.default === "object" ? JSON.stringify(prop.default) : String(prop.default)}`);
    return parts.join(" · ");
}

/**
 * Resolved schema rendering — follows $ref into components.schemas
 * and renders properties/types/required/constraints inline.
 *
 * NOTE: self-references use `React.createElement(ResolvedSchema, …)`
 * on purpose. See the file header for the rationale (visual-edits
 * Babel plugin bug).
 */
export function ResolvedSchema({ schema, spec, depth = 0 }) {
    if (!schema) return null;
    if (schema.$ref) {
        return React.createElement(ResolvedSchema, {
            schema: inlineSchema(schema, spec),
            spec,
            depth,
        });
    }
    if (schema.type === "array") {
        return (
            <div className="text-[11px] text-white/60">
                <span className="text-white/80">array</span> of {React.createElement(ResolvedSchema, {
                    schema: schema.items,
                    spec,
                    depth: depth + 1,
                })}
            </div>
        );
    }
    if (schema.enum) return <span className="text-[11px] text-amber-300">enum: {schema.enum.join(" | ")}</span>;
    if (schema.anyOf || schema.oneOf) {
        const arr = schema.anyOf || schema.oneOf;
        return (
            <div className="text-[11px] text-white/60 space-y-1">
                <div className="text-white/40">one of:</div>
                {arr.map((sub, i) => (
                    <div key={i} className="pl-3 border-l border-white/10">
                        {React.createElement(ResolvedSchema, { schema: sub, spec, depth: depth + 1 })}
                    </div>
                ))}
            </div>
        );
    }
    if (schema.type === "object" || schema.properties) {
        const required = new Set(schema.required || []);
        const props = schema.properties || {};
        const propEntries = Object.entries(props);
        if (!propEntries.length)
            return <div className="text-[11px] text-white/40">{schema.title ? `${schema.title} (no properties)` : "object"}</div>;
        return (
            <div className="rounded-md border border-white/10 overflow-hidden">
                {schema.title && <div className="px-3 py-1.5 bg-white/[0.04] text-[11px] text-white/60 font-mono border-b border-white/10">{schema.title}</div>}
                <table className="w-full text-[11px]">
                    <tbody>
                        {propEntries.map(([name, prop]) => {
                            const resolved = inlineSchema(prop, spec);
                            const nested = (resolved.type === "object" && resolved.properties)
                                ? React.createElement(ResolvedSchema, { schema: resolved, spec, depth: depth + 1 })
                                : null;
                            return (
                                <tr key={name} className="border-t border-white/5 first:border-t-0">
                                    <td className="px-3 py-1.5 align-top w-1/4 font-mono text-white/85">
                                        {name}{required.has(name) && <span className="text-rose-400 ml-1">*</span>}
                                    </td>
                                    <td className="px-3 py-1.5 align-top text-white/60 font-mono">{describeType(resolved)}</td>
                                    <td className="px-3 py-1.5 align-top text-white/55">
                                        {resolved.description && <div className="whitespace-pre-wrap">{resolved.description}</div>}
                                        {describeConstraints(resolved) && (
                                            <div className="text-[10px] text-white/35 mt-0.5">{describeConstraints(resolved)}</div>
                                        )}
                                        {nested && (
                                            <details className="mt-1">
                                                <summary className="cursor-pointer text-white/50 hover:text-white text-[10px]">nested fields</summary>
                                                <div className="mt-1">{nested}</div>
                                            </details>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        );
    }
    return <span className="text-[11px] text-white/60 font-mono">{describeType(schema)}</span>;
}

/**
 * Catalog of every model in components.schemas. Co-located with
 * ResolvedSchema since it's the only other place that needs the
 * recursive renderer's full output.
 */
export function SchemaCatalog({ spec }) {
    const schemas = spec?.components?.schemas || {};
    const names = Object.keys(schemas).sort();
    const [open, setOpen] = useState({});
    if (!names.length) return null;
    return (
        <section id="schemas" data-testid="api-ref-schemas-section" className="mt-10">
            <div className="mb-3">
                <h3 className="font-display text-xl tracking-tight">Schemas</h3>
                <p className="text-sm text-white/50 mt-1 max-w-3xl">
                    Every Pydantic model referenced by an endpoint, expanded here in alphabetical order.
                    Auto-generated — adding a new model on the backend makes it appear automatically.
                </p>
            </div>
            <div className="rounded-xl border border-white/10 overflow-hidden divide-y divide-white/5">
                {names.map((n) => (
                    <div key={n} data-testid={`schema-${n}`} className="bg-white/[0.02]">
                        <button onClick={() => setOpen((o) => ({ ...o, [n]: !o[n] }))}
                            className="w-full flex items-center justify-between px-4 py-2 text-left hover:bg-white/[0.04]">
                            <code className="text-xs text-white/85">{n}</code>
                            <span className="text-[10px] text-white/40">{open[n] ? "hide" : "show"}</span>
                        </button>
                        {open[n] && (
                            <div className="px-4 pb-3">
                                {React.createElement(ResolvedSchema, { schema: schemas[n], spec })}
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </section>
    );
}
